import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../client';
import type { ApiError } from '../types/common';

export interface AdminTherapist {
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone: string | null;
  status: string;
  verificationStatus: string;
  ratingAvg: number;
  reviewCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AdminTherapistDetail extends AdminTherapist {
  bio: string | null;
  sessionCount: number;
  totalReviews: number;
  submittedAt?: string;
  verifiedAt?: string;
  verifiedBy?: { id: string; firstName: string; lastName: string };
  rejectionReason?: string;
  professionalInfo?: {
    npiNumber?: string;
    licenseType?: string;
    licenseState?: string;
    licenseNumber?: string;
  };
  documents: Array<{
    id: string;
    documentType: string;
    fileName: string;
    contentType: string;
    fileSize: number;
    status: string;
    uploadedAt: string;
    processedAt?: string;
    extractedData?: Record<string, unknown>;
  }>;
}

export interface AdminTherapistsResponse {
  therapists: AdminTherapist[];
  total: number;
  page: number;
  limit: number;
}

export interface AdminTherapistsFilters {
  search?: string;
  verification_status?: string;
  page?: number;
  limit?: number;
}

const adminTherapistKeys = {
  all: ['admin-therapists'] as const,
  list: (filters?: AdminTherapistsFilters) => [...adminTherapistKeys.all, 'list', filters] as const,
  detail: (id: string) => [...adminTherapistKeys.all, 'detail', id] as const,
};

async function fetchAdminTherapists(filters?: AdminTherapistsFilters): Promise<AdminTherapistsResponse> {
  const params: Record<string, string> = {};
  if (filters?.search) params.search = filters.search;
  if (filters?.verification_status) params.verification_status = filters.verification_status;
  if (filters?.page) params.page = String(filters.page);
  if (filters?.limit) params.limit = String(filters.limit);

  const response = await apiClient.get<AdminTherapistsResponse>('/admin/therapists', { params });
  return response.data;
}

async function fetchAdminTherapistDetail(id: string): Promise<AdminTherapistDetail> {
  const response = await apiClient.get<AdminTherapistDetail>(`/admin/therapists/${id}`);
  return response.data;
}

async function updateTherapistStatus(id: string, status: string): Promise<{ message: string; therapistId: string; status: string }> {
  const response = await apiClient.put(`/admin/therapists/${id}/status`, { status });
  return response.data;
}

export function useAdminTherapists(filters?: AdminTherapistsFilters) {
  return useQuery<AdminTherapistsResponse, ApiError>({
    queryKey: adminTherapistKeys.list(filters),
    queryFn: () => fetchAdminTherapists(filters),
    staleTime: 30 * 1000,
  });
}

export function useAdminTherapistDetail(id: string) {
  return useQuery<AdminTherapistDetail, ApiError>({
    queryKey: adminTherapistKeys.detail(id),
    queryFn: () => fetchAdminTherapistDetail(id),
    enabled: !!id,
    staleTime: 30 * 1000,
  });
}

export function useUpdateTherapistStatus() {
  const queryClient = useQueryClient();

  return useMutation<{ message: string; therapistId: string; status: string }, ApiError, { id: string; status: string }>({
    mutationFn: ({ id, status }) => updateTherapistStatus(id, status),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: adminTherapistKeys.all });
      queryClient.invalidateQueries({ queryKey: adminTherapistKeys.detail(id) });
    },
  });
}
